/* eslint-disable arrow-body-style */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { createPollEvent } from '../../analytics/AnalyticsEvents';
import { sendAnalytics } from '../../analytics/functions';
import { getLocalParticipant } from '../../base/participants/functions';
import { hasIdenticalAnswers } from '../functions';
import { COMMAND_NEW_POLL, COMMAND_UPDATE_POLL } from '../constants';
import { NOTIFICATION_TIMEOUT_TYPE, showNotification } from '../../notifications';
import { editPoll as editPollFunc } from '../actions';
import { TIME } from './web/constant';
import { isLocalParticipantCanPoll } from '../../poll-moderation/functions';
/**
 * Higher Order Component taking in a concrete PollCreate component and
 * augmenting it with state/behavior common to both web and native implementations.
 *
 * @param {React.AbstractComponent} Component - The concrete component.
 * @returns {React.AbstractComponent}
 */
const AbstractPollCreate = (Component) => (props) => {
    const { setCreateMode } = props;
    const pollState = useSelector((state) => state['features/polls'].polls);
    const editingPoll = useMemo(() => {
        if (!pollState) {
            return null;
        }
        for (const key in pollState) {
            if (pollState.hasOwnProperty(key) && pollState[key].editing) {
                return [key, pollState[key]];
            }
        }
        return null;
    }, [pollState]);
    const answerResults = useMemo(() => {
        return editingPoll
            ? editingPoll[1].answers
            : [
                {
                    name: '',
                    voters: []
                },
                {
                    name: '',
                    voters: []
                }
            ];
    }, [editingPoll]);
    const questionResult = useMemo(() => {
        return editingPoll ? editingPoll[1].question : '';
    }, [editingPoll]);
    const [question, setQuestion] = useState(questionResult);
    const [answers, setAnswers] = useState(answerResults);
    const [validateQuestion, setValidateQuestion] = useState({
        empty: true,
        overMaxLength: false
    });
    const [options, setOptions] = useState({
        chooseMutiple: false,
        addMoreOptions: false,
        voteAnonymous: false,
        setUpTime: false
    });
    const [duration, setDuration] = useState(TIME.FIFTEEN_MINUTES);
    const [clickSubmit, setClickSubmit] = useState(false);
    const findCommonElements = (array, key) => {
        const elementCounts = {};
        // Count occurrences of each element based on the specified key
        array.forEach((obj) => {
            const value = obj[key];
            elementCounts[value] = (elementCounts[value] || 0) + 1;
        });
        // Filter elements that occur more than once
        return Object.keys(elementCounts).filter((value) => elementCounts[value] > 1);
    };
    const canCreatePolls = useSelector(isLocalParticipantCanPoll);
    const handleDuration = (value) => {
        setDuration(value);
    };
    const setAnswer = useCallback((i, answer) => {
        setAnswers((currentAnswers) => {
            const newAnswers = [...currentAnswers];
            newAnswers[i] = answer;
            return newAnswers;
        });
    }, [answers]);
    const addAnswer = useCallback((i) => {
        const newAnswers = [...answers];
        sendAnalytics(createPollEvent('option.added'));
        newAnswers.splice(typeof i === 'number'
            ? i : answers.length, 0, {
            name: '',
            voters: []
        });
        setAnswers(newAnswers);
    }, [answers]);
    const removeAnswer = useCallback(i => {
        if (answers.length <= 2) {
            return;
        }
        const newAnswers = [...answers];
        sendAnalytics(createPollEvent('option.removed'));
        newAnswers.splice(i, 1);
        setAnswers(newAnswers);
    }, [answers]);
    const { conference } = useSelector((state) => state['features/base/conference']);
    const dispatch = useDispatch();
    const pollId = Math.floor(Math.random() * Number.MAX_SAFE_INTEGER).toString(36);
    const localParticipant = useSelector(getLocalParticipant);
    const onSubmit = useCallback(ev => {
        if (ev) {
            ev.preventDefault();
        }
        if (!canCreatePolls) {
            dispatch(showNotification({
                descriptionKey: "polls.notify.lockStartPollTitle",
                titleKey: t("polls.notify.lockStartPoll"),
            }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
            return;
        }
        !clickSubmit && setClickSubmit(true);
        const filteredAnswers = answers.filter((answer) => answer.name.trim().length > 0).map((ans) => ans.name);
        if (filteredAnswers.length < 2) {
            return;
        }
        if (validateQuestion.empty || validateQuestion.overMaxLength) {
            return;
        }
        //Check answers are same
        if (findCommonElements(answers, 'name')?.length > 0) {
            return;
        }
        if (answers.some((answer) => answer?.name.trim()?.length === 0)) {
            return;
        }
        if (answers.some((answer) => answer?.name.trim()?.length > 255)) {
            return;
        }
        sendAnalytics(createPollEvent('created'));
        const idRandom = Math.floor(Math.random() * Number.MAX_SAFE_INTEGER).toString(36);
        conference?.sendPollMessage({
            type: COMMAND_NEW_POLL,
            poll_id: idRandom,
            duration: options.setUpTime ? duration : -1,
            anonymous_poll: options.voteAnonymous,
            question: `${question}`,
            choose_multiple_options: options.chooseMutiple,
            can_add_option: options.addMoreOptions,
            answers: filteredAnswers,
        });
        setCreateMode(false);
    }, [conference, question, answers, options, duration, canCreatePolls]);
    // Check if the poll create form can be submitted i.e. if the send button should be disabled.
    const isSubmitDisabled = question.trim().length <= 0 // If no question is provided
        || answers.filter((answer) => (answer.name || '').trim().length > 0).length < 2 // If not enough options are provided
        || hasIdenticalAnswers(answers); // If duplicate options are provided
    const { t } = useTranslation();
    const onSubmitUpdatePoll = (ev, pollId) => {
        if (APP.store.getState()['features/polls'].polls[pollId]?.status === 'not_start') {
            if (ev) {
                ev.preventDefault();
            }
            !clickSubmit && setClickSubmit(true);
            const filteredAnswers = answers.filter((answer) => answer.name.trim().length > 0).map((ans) => ans.name);
            ;
            if (filteredAnswers.length < 2) {
                return;
            }
            if (!validateQuestion) {
                return;
            }
            //Check answers are same
            if (findCommonElements(answers, 'name')?.length > 0) {
                return;
            }
            conference?.sendPollMessage({
                type: COMMAND_UPDATE_POLL,
                poll_id: pollId,
                duration: options.setUpTime ? duration : -1,
                anonymous_poll: options.voteAnonymous,
                question,
                choose_multiple_options: options.chooseMutiple,
                can_add_option: options.addMoreOptions,
                answers: filteredAnswers
            });
        }
        else if (APP.store.getState()['features/polls'].polls[pollId]?.status === 'started') {
            dispatch(showNotification({
                descriptionKey: "notify.updatePollContent",
                titleKey: "notify.updatePollTitle",
            }, NOTIFICATION_TIMEOUT_TYPE.LONG));
        }
        if (!APP.store.getState()['features/polls'].polls[pollId]) {
            dispatch(showNotification({
                descriptionKey: "notify.deleted",
                titleKey: "notify.updatePollTitle",
            }, NOTIFICATION_TIMEOUT_TYPE.LONG));
        }
        sendAnalytics(createPollEvent('created'));
        dispatch(editPollFunc(''));
        setCreateMode(false);
    };
    useEffect(() => {
        if (question.trim().length > 0 && question.trim().length <= 255) {
            setValidateQuestion((prev) => ({ overMaxLength: false, empty: false }));
        }
        else if (question.trim().length > 0 && question.trim().length > 255) {
            setValidateQuestion((prev) => ({ overMaxLength: true, empty: false }));
        }
        else
            setValidateQuestion((prev) => ({ overMaxLength: false, empty: true }));
    }, [question]);
    return (React.createElement(Component, { addAnswer: addAnswer, answers: answers, editingPoll: editingPoll?.[1], editingPollId: editingPoll?.[0], isSubmitDisabled: isSubmitDisabled, onSubmit: onSubmit, question: question, removeAnswer: removeAnswer, setAnswer: setAnswer, setCreateMode: setCreateMode, setQuestion: setQuestion, options: options, setOptions: setOptions, onSubmitUpdatePoll: onSubmitUpdatePoll, setAnswers: setAnswers, duration: duration, handleDuration: handleDuration, validateQuestion: validateQuestion, setValidateQuestion: setValidateQuestion, clickSubmit: clickSubmit, t: t }));
};
export default AbstractPollCreate;
